import { useContext } from 'react';
import {
  Form,
  FieldSet,
  Button,
  ColumnLayout,
  Column,
  Typography,
  ModalBody,
} from '@visionplanner/ui-react-material';
import PropTypes from 'prop-types';
import isEqual from 'lodash.isequal';
import { firmAddressFormSchema } from './partner-form-validation';
import { StyledModalFooter } from '../../styles';
import { PartnerContext } from '../partner-context';
import { saveFirmAddress } from '../partner-onboarding.requests';
import { types } from '../partner-onboarding.reducer';
import { useAsyncCallback } from '../../../common/custom-hooks/use-async-callback';
import useInternationalizationConfiguration from '../../../common/custom-hooks/use-internationalization-configuration';
import { region } from '../../../common/enums';

/** Component to update firm-address step details */

const FirmAddress = ({ validations, countryTypes }) => {
  const {
    activeStep,
    setActiveStep,
    state,
    dispatch,
  } = useContext(PartnerContext);
  const { partnerId, firmAddress } = state;

  const { isRegion, configuration } = useInternationalizationConfiguration();
  const isBERegion = isRegion(region.BE);
  const regionId = configuration.region;
  const firmAddressFormRegionSchema = validations && firmAddressFormSchema({
    postalCodeRegex: validations.postalCodeRegex, vatNumberRegex: validations.vatNumberRegex, telephoneNumberRegex: validations.telephoneNumberRegex,
  });

  const handleSubmit = useAsyncCallback(async (formValues) => {
    if (!isEqual(firmAddress, formValues)) {
      await saveFirmAddress(partnerId, formValues);
      dispatch({
        type: types.saveFirmAddress,
        payload: formValues,
      });
    }
    setActiveStep(activeStep + 1);
  }, [activeStep, dispatch, firmAddress, partnerId, setActiveStep]);

  return (
    <>
      <Form
        initialValues={{ ...firmAddress, countryTypeId: regionId, countryId: regionId }}
        validateFormSchema={firmAddressFormRegionSchema}
        onSubmit={handleSubmit}
        dataTa="firm-address-form"
        isInitiallyValid={firmAddressFormRegionSchema?.isValidSync(firmAddress)}
        className="full-height flex-column"
      >
        {
          ({
            isValid, isSubmitting,
          }) => (
            <>
              <ModalBody>
                <Typography use="h6">Kantooradres</Typography>
                <ColumnLayout>
                  <Column span={3}>
                    <FieldSet
                      type="selectOne"
                      controlType="autocomplete"
                      label="Land"
                      name="countryTypeId"
                      options={countryTypes.filter((type) => type.value === regionId)}
                      disabled
                      dataTa="country-type"
                      noOptionsMessage="Geen opties"
                    />
                  </Column>
                  {/** As we are only targetting Netherlands for now, commented other countries API call and code */}
                  {/* {values.countryTypeId === OTHERS_COUNTRY_TYPE
                  && (
                    <Column span={4}>
                      <FieldSet
                        type="selectOne"
                        controlType="dropdown"
                        label="Selecteren Land"
                        name="countryId"
                        placeholder="Selecteren land"
                        options={countries.filter(country => country.value !== FILTER_NETHERLANDS_FROM_LIST)}
                        dataTa="select-country"
                        required
                      />
                    </Column>
                  )
                } */}
                </ColumnLayout>
                <ColumnLayout>
                  <Column span={2}>
                    <FieldSet
                      type="text"
                      name="postalCode"
                      label={isBERegion ? 'Postnummer' : 'Postcode'}
                      required
                      dataTa="postal-code"
                    />
                  </Column>
                  <Column span={3}>
                    <FieldSet
                      type="text"
                      name="houseNumber"
                      label="Huisnummer"
                      required
                      dataTa="house-number"
                    />
                  </Column>
                </ColumnLayout>
                <ColumnLayout>
                  <Column span={9}>
                    <FieldSet
                      type="text"
                      name="streetName"
                      label="Straatnaam"
                      required
                      dataTa="street-name"
                    />
                  </Column>
                </ColumnLayout>
                <ColumnLayout>
                  <Column span={4}>
                    <FieldSet
                      type="text"
                      name="city"
                      label="Plaats"
                      required
                      dataTa="city"
                    />
                  </Column>
                </ColumnLayout>
                <ColumnLayout>
                  <Column span={9}>
                    <FieldSet
                      type="email"
                      name="email"
                      label="Kantoor E-mailadres"
                      required
                      dataTa="firm-email"
                    />
                  </Column>
                </ColumnLayout>
                <ColumnLayout>
                  <Column span={4}>
                    <FieldSet
                      type="tel"
                      name="phoneNumber"
                      label="Kantoor telefoonnummer"
                      required
                      dataTa="firm-phone-number"
                    />
                  </Column>
                </ColumnLayout>
                <ColumnLayout>
                  <Column span={4}>
                    <FieldSet
                      type="text"
                      name="vatNumber"
                      label="BTW-nummer"
                      required={isBERegion}
                      dataTa="firm-vat-number"
                    />
                  </Column>
                </ColumnLayout>
              </ModalBody>
              <StyledModalFooter>
                <Button buttonType="secondary" dataTa="go-to-firm-key-details" onClick={() => setActiveStep(activeStep - 1)}>Vorige</Button>
                <Button type="submit" dataTa="go-to-add-employee" disabled={!isValid || isSubmitting}>Volgende</Button>
              </StyledModalFooter>
            </>
          )
        }
      </Form>
    </>
  );
};

FirmAddress.propTypes = {
  /** Country type options */
  countryTypes: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};

export default FirmAddress;
